import Vue from 'vue';
import VueRouter, { RouteConfig, Route, NavigationGuardNext } from 'vue-router';

import Login from '../views/Account/Login.vue';
import ResetPassword from '../views/Account/ResetPassword.vue';
import ResendVerificationEmail from '../views/Account/ResendVerificationEmail.vue';
import Signup from '../views/Account/Signup.vue';

import DeviceOverview from '../views/Device/DeviceOverview.vue';
import DeviceSettings from '../views/Device/DeviceSettings.vue';

import DeviceBasicSettings from '@/components/devices/settings/DeviceBasicSettings.vue';
import DeviceAlertsSettings from '@/components/devices/settings/DeviceAlertsSettings.vue';
import DeviceSecuritySettings from '@/components/devices/settings/DeviceSecuritySettings.vue';
import DeviceSharingSettings from '@/components/devices/settings/DeviceSharingSettings.vue';
import DevicePlanSettings from '@/components/devices/settings/DevicePlanSettings.vue';

import UserSettings from '../views/UserSettings/UserSettings.vue';
import DevicesTransfer from '../views/DevicesTransfer/DevicesTransfer.vue';

import UserAccountSettings from '@/components/users/settings/UserAccountSettings.vue';
import UserPaymentSettings from '@/components/users/settings/UserPaymentSettings.vue';

import Support from '../views/Support/Support.vue';
import Privacy from '../views/Privacy/Privacy.vue';

import store from '@/store';
import { actions } from '@/store/types';

import _get from 'lodash.get';
import ShareUsers from '@/views/ShareUsers/ShareUsers.vue';
import DevicesActivity from '@/views/DevicesActivity/DevicesActivity.vue';
import GroupDevices from '@/views/GroupDevices/GroupDevices.vue';
import DeviceGroup from '@/views/GroupDevices/DeviceGroup.vue';
import CreateGroupDevices from '@/views/GroupDevices/CreateGroupDevices.vue';
import EditGroupDevices from '@/views/GroupDevices/EditGroupDevices.vue';

Vue.use(VueRouter);

const routes: Array<RouteConfig> = [
    // Account
    {
        path: '/login',
        name: 'account-login',
        component: Login,
        meta: {
            title: 'Login',
        },
    },
    {
        path: '/reset',
        name: 'account-reset',
        component: ResetPassword,
        meta: {
            title: 'Reset Password',
        },
    },
    {
        path: '/resendVerificationEmail/:email?',
        name: 'verificationEmail-resend',
        component: ResendVerificationEmail,
        meta: {
            title: 'Resend Verification Email',
        },
    },
    {
        path: '/signup',
        name: 'account-signup',
        component: Signup,
        meta: {
            title: 'Signup',
        },
    },

    // General
    {
        path: '/device/:deviceUUID?',
        alias: ['/'],
        name: 'device-overview',
        component: DeviceOverview,
        meta: {
            auth: true,
            title: 'AirBolts',
            docTitle: 'Device overview',
        },
    },
    {
        path: '/device/:deviceUUID/settings',
        name: 'device-settings',
        redirect: { name: 'device-settings-basic' },
        component: DeviceSettings,
        meta: {
            auth: true,
            title: 'Device settings',
        },
        children: [
            {
                path: 'basic',
                name: 'device-settings-basic',
                component: DeviceBasicSettings,
                props: { page: 'Page #1' },
            },
            {
                path: 'alert',
                name: 'device-settings-alert',
                component: DeviceAlertsSettings,
                props: { page: 'Page #2' },
            },
            {
                path: 'security',
                name: 'device-settings-security',
                component: DeviceSecuritySettings,
                props: { page: 'Page #3' },
            },
            {
                path: 'sharing',
                name: 'device-settings-sharing',
                component: DeviceSharingSettings,
                props: { page: 'Page #4' },
            },
            {
                path: 'plans',
                name: 'device-settings-plans',
                component: DevicePlanSettings,
                props: { page: 'Page #5' },
            },
        ],
    },
    {
        path: '/user',
        name: 'user-settings',
        redirect: { name: 'user-settings-account' },
        component: UserSettings,
        meta: {
            auth: true,
            title: 'Account',
        },
        children: [
            {
                path: 'account',
                name: 'user-settings-account',
                component: UserAccountSettings,
            },
            {
                path: 'payments',
                name: 'user-settings-payments',
                component: UserPaymentSettings,
            },
        ],
    },
    {
        path: '/transfer/devices',
        name: 'transfer-devices',
        component: DevicesTransfer,
        meta: {
            auth: true,
            title: 'Transfer Devices',
        },
    },
    // {
    //     path: '/share/account',
    //     name: 'share-account',
    //     component: ShareUsers,
    //     meta: {
    //         auth: true,
    //         title: 'User',
    //     },
    // },

    {
        path: '/devices/activity',
        name: 'device-activity',
        component: DevicesActivity,
        meta: {
            auth: true,
            title: 'Devices Activity',
        },
    },

    // Devices Group
    {
        path: '/group/devices',
        name: 'group-devices',
        component: GroupDevices,
        meta: {
            auth: true,
            title: 'Group Devices',
        },
    },
    {
        path: '/group/devices/create',
        name: 'create-group-devices',
        component: CreateGroupDevices,
        meta: {
            auth: true,
            title: 'Create Group Devices',
        },
    },
    {
        path: '/group/devices/edit/:groupId',
        name: 'edit-group-device',
        component: EditGroupDevices,
        meta: {
            auth: true,
            title: 'Groups',
            docTitle: 'Edit Group Device',
        },
    },
    {
        path: '/group/devices/:groupId',
        alias: ['/'],
        name: 'group-device',
        component: DeviceGroup,
        meta: {
            auth: true,
            title: 'Groups',
            docTitle: 'Group Device',
        },
    },

    // Help & support
    {
        path: '/support',
        name: 'support',
        component: Support,
        meta: {
            auth: true,
            title: 'Support',
        },
    },

    // Privacy & terms
    {
        path: '/privacyAndTerms',
        name: 'privacy',
        component: Privacy,
        meta: {
            title: 'Privacy & terms',
        },
    },
];

const router = new VueRouter({
    mode: 'history',
    base: process.env.BASE_URL,
    routes,
});

router.beforeEach(async (to: Route, from: Route, next: NavigationGuardNext) => {
    const title = _get(to, 'meta.docTitle') || _get(to, 'meta.title');
    document.title = title ? `AirBolt :: ${title}` : `AirBolt`;
    const authRequired = to.matched.some((m) => _get(m, 'meta.auth'));
    if (!authRequired) return next();
    if (!store.getters.isLoggedOn)
        await store.dispatch(actions.CHECK_AUTH_TOKEN, {
            next: to.name,
            params: to.params,
            sessionToken: to.query?.session ?? '',
        });
    if (!store.getters.isLoggedOn) return next({ name: 'account-login' });
    return next();
});

export default router;
